/**
 * useHistoricalTrendsData — fetches trailing-N-period metrics for the
 * Historical Trends tab.
 *
 * For each of N periods (aligned to the global D/W/M/Q/H/Y selection) this
 * fires a parallel call to getASFMPreProcessedData (the same query the
 * Business Metrics tab uses) and rolls the per-category rows into the
 * three groups we render: Search (P1 / P2+ / Total), Detail (Total),
 * Homepage (Total — SP only).
 */

import { useState, useEffect, useRef, useCallback } from "react";
import type { DashboardFilters } from "../../types";
import {
  getTrailingPeriods,
  computeBaselineDates,
  type AlignmentType,
  type ComparisonModeType,
} from "../../utils/dateUtils";
import { getMarketplaceList } from "../../utils/marketplaceUtils";
import {
  fetchPreProcessedData,
  fetchMarketplaces,
  fetchRegions,
} from "../../services/dashboardService";
import type { ReportData, AnalysisMetadata } from "../../utils/reportGenerator";
import { generateReportFromData } from "../../utils/reportGenerator";

const DEFAULT_TRAILING_COUNT = 9;

/**
 * Max periods fetched at once. Each period issues TWO queries (target +
 * baseline), so this caps in-flight Athena/Lambda calls at 2×. Firing every
 * period in parallel (e.g. 31 days → 62 calls) gets calls throttled/dropped,
 * which surfaced as randomly blank columns.
 */
const FETCH_CONCURRENCY = 6;

/**
 * Map over items with a bounded number of concurrent workers, preserving the
 * input order in the results.
 */
async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  worker: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  const runners = Array.from(
    { length: Math.min(Math.max(1, limit), items.length) },
    async () => {
      for (;;) {
        const i = next++;
        if (i >= items.length) return;
        results[i] = await worker(items[i], i);
      }
    }
  );
  await Promise.all(runners);
  return results;
}

/** Run a fetch, retrying once on failure (covers transient throttling). */
async function fetchWithRetry<R>(
  run: () => Promise<R>,
  label: string,
  kind: string
): Promise<R | null> {
  try {
    return await run();
  } catch (first: any) {
    try {
      return await run();
    } catch (err: any) {
      console.warn(
        `[HistoricalTrends] ${kind} fetch failed after retry`,
        label,
        err
      );
      return null;
    }
  }
}

/** USD-denominated metrics we surface per period and group. */
export interface PeriodMetrics {
  revenue_usd: number | null;
  revenue_loc: number | null;
  total_page_requests: number | null;
  rpom: number | null;
  rpom_loc: number | null;
  coverage: number | null;
  cpc: number | null;
  cpc_loc: number | null;
  ad_count: number | null;
  ad_ctr: number | null;
  // SB-only video / static splits — left optional, not surfaced in v1.
  video_revenue_usd?: number | null;
  static_revenue_usd?: number | null;
  video_revenue_loc?: number | null;
  static_revenue_loc?: number | null;
  traffic?: number | null;
}

/** Metrics for a single period column. */
export interface PeriodColumn {
  /** Display label, e.g. "W/E 05/10". */
  label: string;
  /** Aligned start date YYYY-MM-DD. */
  start: string;
  /** Aligned end date YYYY-MM-DD. */
  end: string;
  /** Baseline range for this period (driven by global comparisonMode). */
  baselineStart: string;
  baselineEnd: string;
  searchTotal: PeriodMetrics;
  searchP1: PeriodMetrics;
  searchP2: PeriodMetrics;
  detailTotal: PeriodMetrics;
  /** Empty for SB. */
  homepageTotal: PeriodMetrics;
  /** Same shape as the target metrics, computed from the baseline rows. */
  baselineSearchTotal: PeriodMetrics;
  baselineSearchP1: PeriodMetrics;
  baselineSearchP2: PeriodMetrics;
  baselineDetailTotal: PeriodMetrics;
  baselineHomepageTotal: PeriodMetrics;
}

/**
 * Full main-tab fidelity per period — produced by `generateReportFromData`
 * so the numbers match Business Metrics / Metric Changes / Revenue Bridge /
 * Growth Bridge tabs exactly when the same (alignment, comparisonMode) is
 * applied. Used by the Historical Trends sub-tab views.
 */
export interface PeriodReport {
  label: string;
  start: string;
  end: string;
  baselineStart: string;
  baselineEnd: string;
  reportData: ReportData;
}

export interface HistoricalTrendsResult {
  periods: PeriodColumn[];
  reports: PeriodReport[];
  isLoading: boolean;
  error: string | null;
  /** Labels of periods whose query failed (their columns render blank). */
  failedPeriods: string[];
}

const EMPTY_METRICS: PeriodMetrics = {
  revenue_usd: null,
  revenue_loc: null,
  total_page_requests: null,
  rpom: null,
  rpom_loc: null,
  coverage: null,
  cpc: null,
  cpc_loc: null,
  ad_count: null,
  ad_ctr: null,
};

/** Convert a string Athena value to a number, treating empties as null. */
function num(v: any): number | null {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

/** Sum two nullable numbers (null acts as 0 if the other side is non-null). */
function sumNullable(a: number | null, b: number | null): number | null {
  if (a == null && b == null) return null;
  return (a ?? 0) + (b ?? 0);
}

/** Parse Athena result envelope into row objects. Mirrors useSPBAnalyticsData. */
function parseAthenaResult(result: any): any[] {
  if (!result) return [];
  if (Array.isArray(result)) return result;
  const rows: any[] = result.rows || [];
  if (rows.length === 0) return [];
  if (!Array.isArray(rows[0])) return rows;
  const columns: string[] = (result.columns || []).map((c: any) => c.name || c);
  return rows.map((row: any[]) => {
    const obj: Record<string, string> = {};
    columns.forEach((col: string, i: number) => {
      obj[col] = row[i] != null ? String(row[i]) : "";
    });
    return obj;
  });
}

/**
 * Pull a row out of the Athena response that matches a given page_type and
 * device_type. The same response carries multiple categories (Search,
 * Detail, Homepage, Search+Detail+Homepage) thanks to GROUPING SETS.
 */
function findRow(
  rows: any[],
  pageType: string,
  deviceType: string
): any | null {
  return (
    rows.find(
      (r) => r.page_type === pageType && r.device_type === deviceType
    ) ?? null
  );
}

/**
 * Extract the "Total" metrics from a category row (Search/Detail/Homepage).
 * Tolerates SP and SB column conventions:
 *   - Page views: SP uses `total_page_requests`; SB uses `traffic`.
 *   - Ad count: SP uses `ad_count` (= ads / filled_widget_requests).
 *               SB uses `page_ad_count` (= impressions / page_impressions).
 *               SB's `ad_density` (= impressions / widget_impressions) is a
 *               narrower rate that doesn't decompose cleanly into P1 / P2+,
 *               so we use `page_ad_count` for the Total/P1/P2+ rows.
 *   - Ad CTR:   SP and SB both use `ad_ctr` (clicks / ad-impressions) so the
 *               RPOM driver decomposition telescopes correctly.
 *   - Video / Static revenue: SB-only, optional on the type.
 */
function extractSearchTotal(row: any | null): PeriodMetrics {
  if (!row) return { ...EMPTY_METRICS };
  return {
    revenue_usd: num(row.revenue_usd),
    revenue_loc: num(row.revenue_loc),
    total_page_requests: num(row.total_page_requests) ?? num(row.traffic),
    rpom: num(row.rpom),
    rpom_loc: num(row.rpom_loc),
    coverage: num(row.coverage),
    cpc: num(row.cpc),
    cpc_loc: num(row.cpc_loc),
    // SP: ad_count. SB: prefer page_ad_count (matches main-tab Total row,
    // see reportGenerator.ts:566). Falls back to ad_density only if neither
    // is present.
    ad_count:
      num(row.ad_count) ?? num(row.page_ad_count) ?? num(row.ad_density),
    // SB Total uses ad_ctr (= clicks / ad-impressions) — matches the main-tab
    // Total Search/Detail row and keeps Coverage x Ad Count x Ad CTR x CPC
    // telescoping to RPOM. SP rows already expose ad_ctr (= clicks / ads); the
    // page_ad_ctr fallback is retained only for older cached rows.
    ad_ctr: num(row.ad_ctr) ?? num(row.page_ad_ctr),
    video_revenue_usd: num(row.video_revenue_usd),
    static_revenue_usd: num(row.static_revenue_usd),
    video_revenue_loc: num(row.video_revenue_loc),
    static_revenue_loc: num(row.static_revenue_loc),
    traffic: num(row.traffic),
  };
}

/**
 * Extract the SP "Search P1" metrics. The SP query_store.py now emits
 * p1_cpc / p1_coverage / p1_ad_count / p1_ad_ctr (and p1_rpom / p1_rpom_loc)
 * directly on the Search row, so we read them straight through.
 */
function extractSearchP1SP(row: any | null): PeriodMetrics {
  if (!row) return { ...EMPTY_METRICS };
  const rev = num(row.p1_revenue_usd);
  const revLoc = num(row.p1_revenue_loc);
  const pv = num(row.total_p1_page_requests);
  // Prefer pre-computed rpom from SQL; fall back to client-side division.
  const rpom =
    num(row.p1_rpom) ?? (pv && rev != null ? (rev * 1000) / pv : null);
  const rpomLoc =
    num(row.p1_rpom_loc) ??
    (pv && revLoc != null ? (revLoc * 1000) / pv : null);
  return {
    revenue_usd: rev,
    revenue_loc: revLoc,
    total_page_requests: pv,
    rpom,
    rpom_loc: rpomLoc,
    coverage: num(row.p1_coverage),
    cpc: num(row.p1_cpc),
    cpc_loc: num(row.p1_cpc_loc),
    ad_count: num(row.p1_ad_count),
    ad_ctr: num(row.p1_ad_ctr),
  };
}

/**
 * SP "Search P2+" — query_store.py now emits p2_cpc / p2_coverage /
 * p2_ad_count / p2_ad_ctr (derived as total - p1 in SQL). Revenue and
 * page-views still need client-side derivation since the preprocess
 * table doesn't carry an explicit p2_revenue_usd aggregate.
 */
function extractSearchP2SP(row: any | null): PeriodMetrics {
  if (!row) return { ...EMPTY_METRICS };
  const total = num(row.revenue_usd);
  const p1 = num(row.p1_revenue_usd);
  const totalLoc = num(row.revenue_loc);
  const p1Loc = num(row.p1_revenue_loc);
  const totalPv = num(row.total_page_requests);
  const p1Pv = num(row.total_p1_page_requests);
  const p2Pv = num(row.total_p2_page_requests);

  const rev = total != null && p1 != null ? total - p1 : null;
  const revLoc = totalLoc != null && p1Loc != null ? totalLoc - p1Loc : null;
  const pv =
    p2Pv != null
      ? p2Pv
      : totalPv != null && p1Pv != null
      ? totalPv - p1Pv
      : null;
  const rpom =
    num(row.p2_rpom) ?? (pv && rev != null ? (rev * 1000) / pv : null);
  const rpomLoc =
    num(row.p2_rpom_loc) ??
    (pv && revLoc != null ? (revLoc * 1000) / pv : null);
  return {
    revenue_usd: rev,
    revenue_loc: revLoc,
    total_page_requests: pv,
    rpom,
    rpom_loc: rpomLoc,
    coverage: num(row.p2_coverage),
    cpc: num(row.p2_cpc),
    cpc_loc: num(row.p2_cpc_loc),
    ad_count: num(row.p2_ad_count),
    ad_ctr: num(row.p2_ad_ctr),
  };
}

/**
 * SB Search P1 / P2 — pulls the per-page rollup columns the SB SQL emits
 * directly: p1_coverage, p1_cpc, p1_ad_density (=ad_count), p1_ad_ctr, etc.
 * SP doesn't emit equivalent rollups, so SP's P1/P2+ leaves these as null.
 * (TODO: add SP rollup columns to the SP preprocess + query_store.py.)
 */
function extractSBSearchByPage(
  row: any | null,
  page: "p1" | "p2"
): PeriodMetrics {
  if (!row) return { ...EMPTY_METRICS };
  const rev = num(row[`${page}_revenue_usd`]);
  const revLoc = num(row[`${page}_revenue_loc`]);
  const pv =
    page === "p1"
      ? num(row.total_p1_page_requests) ?? num(row.p1_traffic)
      : num(row.total_p2_page_requests) ?? num(row.p2_traffic);
  // Prefer the Lambda's pre-computed p1_rpom / p2_rpom columns (matches the
  // main-tab Total Search row math), fall back to client-side derivation
  // only if the column is missing.
  const rpom =
    num(row[`${page}_rpom`]) ?? (pv && rev != null ? (rev * 1000) / pv : null);
  const rpomLoc =
    num(row[`${page}_rpom_loc`]) ??
    (pv && revLoc != null ? (revLoc * 1000) / pv : null);

  // Video / Static revenue split: SB has explicit p1 columns; derive p2 as total - p1.
  const videoP1 = num(row.video_p1_revenue_usd);
  const staticP1 = num(row.static_p1_revenue_usd);
  const videoP1Loc = num(row.video_p1_revenue_loc);
  const staticP1Loc = num(row.static_p1_revenue_loc);
  const videoTotal = num(row.video_revenue_usd);
  const staticTotal = num(row.static_revenue_usd);
  const videoTotalLoc = num(row.video_revenue_loc);
  const staticTotalLoc = num(row.static_revenue_loc);
  const videoRev =
    page === "p1"
      ? videoP1
      : videoTotal != null && videoP1 != null
      ? videoTotal - videoP1
      : null;
  const staticRev =
    page === "p1"
      ? staticP1
      : staticTotal != null && staticP1 != null
      ? staticTotal - staticP1
      : null;
  const videoRevLoc =
    page === "p1"
      ? videoP1Loc
      : videoTotalLoc != null && videoP1Loc != null
      ? videoTotalLoc - videoP1Loc
      : null;
  const staticRevLoc =
    page === "p1"
      ? staticP1Loc
      : staticTotalLoc != null && staticP1Loc != null
      ? staticTotalLoc - staticP1Loc
      : null;

  return {
    revenue_usd: rev,
    revenue_loc: revLoc,
    total_page_requests: pv,
    rpom,
    rpom_loc: rpomLoc,
    coverage: num(row[`${page}_coverage`]),
    cpc: num(row[`${page}_cpc`]),
    cpc_loc: num(row[`${page}_cpc_loc`]),
    // SB ad_count uses page_ad_count (= impressions / page_impressions),
    // matching the main-tab Total Search row. The narrower ad_density
    // (impressions / widget_impressions) doesn't decompose cleanly into
    // P1 / P2+ — Total can fall outside the [P1, P2+] range due to widget
    // overlap. page_ad_count's denominator is per-page so the rates are
    // a clean weighted average.
    ad_count:
      num(row[`${page}_page_ad_count`]) ??
      num(row[`${page}_ad_density`]) ??
      num(row[`${page}_ad_count`]),
    // SB Search P1 / P2+ Ad CTR uses ad_ctr (clicks / ad-impressions) for
    // consistent rate decomposition with the Total Search row (telescopes to
    // RPOM). Falls back to page_ad_ctr only for older cached rows.
    ad_ctr: num(row[`${page}_ad_ctr`]) ?? num(row[`${page}_page_ad_ctr`]),
    video_revenue_usd: videoRev,
    static_revenue_usd: staticRev,
    video_revenue_loc: videoRevLoc,
    static_revenue_loc: staticRevLoc,
    traffic: pv,
  };
}

/** Extract a Detail or Homepage Total row directly. */
function extractTotalRow(row: any | null): PeriodMetrics {
  return extractSearchTotal(row);
}

/** Subset of PeriodColumn that buildPeriodMetrics produces from a single
 *  side (target OR baseline). Wrapped into a full PeriodColumn at call site
 *  by also setting the baseline-side mirrors. */
type PeriodSideMetrics = Pick<
  PeriodColumn,
  | "label"
  | "start"
  | "end"
  | "searchTotal"
  | "searchP1"
  | "searchP2"
  | "detailTotal"
  | "homepageTotal"
>;

/** Build the metrics for a single period from a parsed Athena response. */
function buildPeriodMetrics(
  rows: any[],
  product: "SP" | "SB",
  geoKey: string,
  deviceType: string,
  label: string,
  start: string,
  end: string
): PeriodSideMetrics {
  // The rows array comes back un-keyed by geo when the query rolls up to
  // a single region; tolerate either by filtering on uppercase geo when
  // the field is present.
  const ofGeo = rows.filter((r) => {
    if (!r.geo) return true;
    return String(r.geo).toUpperCase() === geoKey;
  });

  const searchRow = findRow(ofGeo, "Search", deviceType);
  const detailRow = findRow(ofGeo, "Detail", deviceType);
  const homepageRow =
    product === "SP" ? findRow(ofGeo, "Homepage", deviceType) : null;

  let searchP1: PeriodMetrics;
  let searchP2: PeriodMetrics;
  if (product === "SP") {
    searchP1 = extractSearchP1SP(searchRow);
    searchP2 = extractSearchP2SP(searchRow);
  } else {
    searchP1 = extractSBSearchByPage(searchRow, "p1");
    searchP2 = extractSBSearchByPage(searchRow, "p2");
  }

  // Page-views fallback: the Search Total row may not carry total_page_requests
  // when we only have it on the rolled-up "Search+Detail+Homepage" row. Sum P1+P2
  // when missing.
  const searchTotal = extractSearchTotal(searchRow);
  if (searchTotal.total_page_requests == null) {
    searchTotal.total_page_requests = sumNullable(
      searchP1.total_page_requests,
      searchP2.total_page_requests
    );
  }

  return {
    label,
    start,
    end,
    searchTotal,
    searchP1,
    searchP2,
    detailTotal: extractTotalRow(detailRow),
    homepageTotal:
      product === "SP" ? extractTotalRow(homepageRow) : { ...EMPTY_METRICS },
  };
}

/**
 * Hook: fetch trailing N periods of metrics. Triggers a refetch when any
 * of (product, geo, device, alignment, anchor end date) changes.
 */
export function useHistoricalTrendsData(
  filters: Partial<DashboardFilters>,
  trailingCount: number = DEFAULT_TRAILING_COUNT
): HistoricalTrendsResult {
  const [periods, setPeriods] = useState<PeriodColumn[]>([]);
  const [reports, setReports] = useState<PeriodReport[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Labels of periods whose data failed to load (rendered blank) — surfaced so
  // a dropped query is visible instead of looking like missing data.
  const [failedPeriods, setFailedPeriods] = useState<string[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  const product = (filters.product ?? "SP") as "SP" | "SB";
  const geo = filters.geo ?? "ww";
  const deviceFilter = filters.device ?? "Desktop-Mobile";
  const device = deviceFilter.replace("-", "+");
  const alignment = (filters.alignment ?? "W") as AlignmentType;
  const comparisonMode = (filters.comparisonMode ??
    "YoY") as ComparisonModeType;
  const anchorEnd = filters.targetEndDate ?? "";

  const fetchData = useCallback(async () => {
    if (!anchorEnd) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setIsLoading(true);
    setError(null);
    setFailedPeriods([]);

    try {
      const [marketplaces, regions] = await Promise.all([
        fetchMarketplaces(),
        fetchRegions(),
      ]);
      if (controller.signal.aborted) return;

      const { marketplaceIds, geoGroupName } = getMarketplaceList(
        geo,
        regions,
        marketplaces
      );

      const trailing = getTrailingPeriods(anchorEnd, alignment, trailingCount);
      if (trailing.length === 0) {
        setPeriods([]);
        setIsLoading(false);
        return;
      }

      const today = new Date().toISOString().slice(0, 10);
      const baseParams = {
        product,
        geo_list: marketplaceIds,
        geo_group_name: geoGroupName,
        device,
        database: "asfm",
        run_date: today,
      };
      const geoKey = geoGroupName.toUpperCase();
      const pageType =
        product === "SP" ? "Search+Detail+Homepage" : "Search+Detail";

      // Build a `PeriodReport` from raw target+baseline rows for one period.
      // Pure JS — fast — so we recompute on every load (cache stores the
      // raw rows, not the bulky ReportData, to stay under the 400 KB
      // DynamoDB item limit).
      const buildReport = (
        period: { label: string; start: string; end: string },
        baselineRange: { start: string; end: string },
        targetRows: any[],
        baselineRows: any[]
      ): PeriodReport | null => {
        try {
          const metadata: AnalysisMetadata = {
            event_content: {
              page_type: pageType,
              device,
              target_date_range: [period.start, period.end],
              baseline_date_range: [baselineRange.start, baselineRange.end],
            },
          };
          const reportData = generateReportFromData(
            targetRows as any,
            baselineRows as any,
            metadata,
            geoGroupName,
            product
          );
          return {
            label: period.label,
            start: period.start,
            end: period.end,
            baselineStart: baselineRange.start,
            baselineEnd: baselineRange.end,
            reportData,
          };
        } catch (err) {
          console.warn(
            "[HistoricalTrends] generateReportFromData failed",
            period.label,
            err
          );
          return null;
        }
      };

      // For each period, compute its own baseline range based on the selected
      // comparisonMode, and fire two preprocessed_perf_query calls (target +
      // baseline). Each retries once, then falls back to null so a single bad
      // period doesn't poison the rest. Periods run through a bounded worker
      // pool (FETCH_CONCURRENCY) — firing them all at once got calls throttled
      // and produced randomly blank columns at high trailing counts.
      const fetched = await mapWithConcurrency(
        trailing,
        FETCH_CONCURRENCY,
        async (p) => {
          const baseline = computeBaselineDates(p.start, p.end, comparisonMode);
          const dateRangesJson = JSON.stringify({
            target: { start: p.start, end: p.end },
            baseline: { start: baseline.start, end: baseline.end },
          });
          const [targetRes, baselineRes] = await Promise.all([
            fetchWithRetry(
              () =>
                fetchPreProcessedData({
                  ...baseParams,
                  query_name: "preprocessed_perf_query",
                  date_group: "target",
                  date_ranges: dateRangesJson,
                } as any),
              p.label,
              "target"
            ),
            fetchWithRetry(
              () =>
                fetchPreProcessedData({
                  ...baseParams,
                  query_name: "preprocessed_perf_query",
                  date_group: "baseline",
                  date_ranges: dateRangesJson,
                } as any),
              p.label,
              "baseline"
            ),
          ]);
          return { period: p, baseline, targetRes, baselineRes };
        }
      );
      if (controller.signal.aborted) return;

      // Surface periods whose target query still failed — previously these
      // silently rendered as blank columns with no indication anything broke.
      const failedLabels = fetched
        .filter((f) => f.targetRes == null)
        .map((f) => f.period.label);
      setFailedPeriods(failedLabels);

      const cols: PeriodColumn[] = [];
      const reportsArr: PeriodReport[] = [];

      for (const f of fetched) {
        const targetRows = parseAthenaResult(f.targetRes);
        const baselineRows = parseAthenaResult(f.baselineRes);

        const targetCol = buildPeriodMetrics(
          targetRows,
          product,
          geoKey,
          device,
          f.period.label,
          f.period.start,
          f.period.end
        );
        const baselineCol = buildPeriodMetrics(
          baselineRows,
          product,
          geoKey,
          device,
          f.period.label,
          f.baseline.start,
          f.baseline.end
        );
        cols.push({
          ...targetCol,
          baselineStart: f.baseline.start,
          baselineEnd: f.baseline.end,
          baselineSearchTotal: baselineCol.searchTotal,
          baselineSearchP1: baselineCol.searchP1,
          baselineSearchP2: baselineCol.searchP2,
          baselineDetailTotal: baselineCol.detailTotal,
          baselineHomepageTotal: baselineCol.homepageTotal,
        });

        const rep = buildReport(f.period, f.baseline, targetRows, baselineRows);
        if (rep) reportsArr.push(rep);
      }

      setPeriods(cols);
      setReports(reportsArr);
      setIsLoading(false);

      // Historical Trends is not persisted to DynamoDB. The full per-period
      // raw rows needed for accurate Bridge / Growth math exceed the 400 KB
      // item limit (typically ~1.3 MB for 10 weeks × target+baseline). Each
      // tab visit re-fetches; the primary dashboard tabs remain cached as
      // usual.
    } catch (err: any) {
      if (controller.signal.aborted) return;
      console.error("[HistoricalTrends] fetch failed", err);
      setError(err?.message ?? "Failed to load historical trends");
      setIsLoading(false);
    }
  }, [
    product,
    geo,
    device,
    alignment,
    comparisonMode,
    anchorEnd,
    trailingCount,
  ]);

  useEffect(() => {
    fetchData();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchData]);

  return { periods, reports, isLoading, error, failedPeriods };
}
